import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  Filters,
  ChoiceList,
  InlineStack,
  Text,
  Pagination,
  SkeletonBodyText,
  EmptyState,
  Box,
  Tabs,
} from '@shopify/polaris';
import { rentalsApi } from '@/services';
import type { Rental, RentalStatus } from '@/types';
import { orderNumber } from '@/utils/orderNumber';

const ORDER_TABS = [
  { id: 'all', content: 'All Orders', panelID: 'all-orders' },
  { id: 'clinic', content: 'Clinic Orders', panelID: 'clinic-orders' },
  { id: 'direct', content: 'Direct Orders', panelID: 'direct-orders' },
  { id: 'fulfilled_by_clinic', content: 'Fulfilled by Clinic', panelID: 'fulfilled-by-clinic-orders' },
  { id: 'demo', content: 'Demo', panelID: 'demo-orders' },
  { id: 'consignment', content: 'Consignment', panelID: 'consignment-orders' },
  { id: 'probono', content: 'Pro Bono', panelID: 'probono-orders' },
  { id: 'replenishments', content: 'Replenishments', panelID: 'replenishment-orders' },
];

export default function RentalsList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const limit = 20;

  const getTabFilters = useCallback(() => {
    const tabId = ORDER_TABS[selectedTab].id;
    switch (tabId) {
      case 'clinic': return { hasClinic: true as const };
      case 'direct': return { hasClinic: false as const };
      case 'fulfilled_by_clinic': return { isClinicFulfil: true as const };
      case 'demo': return { isDemo: true as const };
      case 'consignment': return { isConsignment: true as const };
      case 'probono': return { isProbono: true as const };
      case 'replenishments': return { isReplenishment: true as const };
      default: return {};
    }
  }, [selectedTab]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['rentals', page, status, searchQuery, selectedTab],
    queryFn: () =>
      rentalsApi.list({
        page,
        limit,
        status: status.length > 0 ? (status as RentalStatus[]) : undefined,
        search: searchQuery || undefined,
        ...getTabFilters(),
      }),
  });

  const handleTabChange = useCallback((index: number) => {
    setSelectedTab(index);
    setPage(1);
  }, []);

  const handleStatusChange = useCallback((value: string[]) => {
    setStatus(value);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatus([]);
    setSearchQuery('');
  }, []);

  const getStatusBadge = (rentalStatus: RentalStatus) => {
    const statusConfig: Record<RentalStatus, { tone: 'success' | 'warning' | 'critical' | 'info' | undefined; label: string }> = {
      pending: { tone: undefined, label: 'Pending' },
      processing: { tone: 'info', label: 'Processing' },
      ready_to_ship: { tone: 'info', label: 'Ready to Ship' },
      shipped: { tone: 'info', label: 'Shipped' },
      in_use: { tone: 'success', label: 'In Use' },
      extended: { tone: 'warning', label: 'Extended' },
      returned: { tone: 'warning', label: 'Returned' },
      complete: { tone: 'success', label: 'Complete' },
      cancelled: { tone: 'critical', label: 'Cancelled' },
      refunded: { tone: 'critical', label: 'Refunded' },
      partial_refund: { tone: 'warning', label: 'Partial Refund' },
      overdue: { tone: 'critical', label: 'Overdue' },
      fulfilled_by_clinic: { tone: 'success', label: 'Fulfilled by Clinic' },
      surgery_cancelled: { tone: 'critical', label: 'Surgery Cancelled' },
    };
    const config = statusConfig[rentalStatus] || { tone: undefined, label: rentalStatus };
    return <Badge tone={config.tone}>{config.label}</Badge>;
  };

  const rows = (data?.data || []).map((rental: Rental) => [
    <InlineStack key={rental.id} gap="200" blockAlign="center" wrap={false}>
      <Button variant="plain" onClick={() => navigate(`/rentals/${rental.id}`)}>
        {orderNumber(rental)}
      </Button>
      {rental.duplicateOfRentalId && !rental.duplicateClearedAt && (
        <Badge tone="critical">Possible duplicate</Badge>
      )}
    </InlineStack>,
    rental.clinic?.name || rental.patientName || 'N/A',
    rental.rentalStartDate ? new Date(rental.rentalStartDate).toLocaleDateString() : 'N/A',
    rental.rentalEndDate ? new Date(rental.rentalEndDate).toLocaleDateString() : 'N/A',
    `${rental.rentalWeeks} weeks`,
    `$${Number(rental.totalAmount).toFixed(2)}`,
    getStatusBadge(rental.rentalStatus),
    <Button key={`view-${rental.id}`} onClick={() => navigate(`/rentals/${rental.id}`)}>
      View
    </Button>,
  ]);

  const filters = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          choices={[
            { label: 'Pending', value: 'pending' },
            { label: 'Processing', value: 'processing' },
            { label: 'Ready to Ship', value: 'ready_to_ship' },
            { label: 'Shipped', value: 'shipped' },
            { label: 'In Use', value: 'in_use' },
            { label: 'Extended', value: 'extended' },
            { label: 'Returned', value: 'returned' },
            { label: 'Complete', value: 'complete' },
            { label: 'Cancelled', value: 'cancelled' },
            { label: 'Overdue', value: 'overdue' },
            { label: 'Fulfilled by Clinic', value: 'fulfilled_by_clinic' },
            { label: 'Surgery Cancelled', value: 'surgery_cancelled' },
            { label: 'Partial Refund', value: 'partial_refund' },
          ]}
          selected={status}
          onChange={handleStatusChange}
          // Cancellation is split across two statuses (cancelled and
          // surgery_cancelled), so "show me the cancelled orders" needs both ticked
          // at once. The API already accepts a comma-separated list.
          allowMultiple
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = status.length > 0
    ? [{ key: 'status', label: `Status: ${status.join(', ')}`, onRemove: () => setStatus([]) }]
    : [];

  if (error) {
    return (
      <Page title="Rentals">
        <Card>
          <EmptyState
            heading="Error loading rentals"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Rentals"
      primaryAction={{
        content: 'New Order',
        onAction: () => navigate('/rentals/new'),
      }}
    >
      <Card padding="0">
        <Tabs tabs={ORDER_TABS} selected={selectedTab} onSelect={handleTabChange} />
        <Box padding="400">
          <Filters
            queryValue={searchQuery}
            queryPlaceholder="Search by order number or customer..."
            filters={filters}
            appliedFilters={appliedFilters}
            onQueryChange={handleSearchChange}
            onQueryClear={() => setSearchQuery('')}
            onClearAll={handleClearFilters}
          />
        </Box>

        {isLoading ? (
          <Box padding="400">
            <SkeletonBodyText lines={10} />
          </Box>
        ) : rows.length === 0 ? (
          <Box padding="800">
            <EmptyState
              heading="No rentals found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>Rentals will appear here when orders are placed.</p>
            </EmptyState>
          </Box>
        ) : (
          <>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
              headings={['Order', 'Customer', 'Start', 'End', 'Duration', 'Total', 'Status', 'Action']}
              rows={rows}
            />
            {data && data.meta.totalPages > 1 && (
              <Box padding="400">
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    hasNext={page < data.meta.totalPages}
                    onPrevious={() => setPage((p) => p - 1)}
                    onNext={() => setPage((p) => p + 1)}
                  />
                </InlineStack>
              </Box>
            )}
          </>
        )}
      </Card>

      {data && (
        <Box paddingBlockStart="400">
          <Text as="p" tone="subdued" alignment="center">
            Showing {data.data.length} of {data.meta.total} rentals
          </Text>
        </Box>
      )}
    </Page>
  );
}
